import { motion } from "framer-motion";
import {
  ExternalLink, ListChecks, ScanLine, Layers, Ruler, Box, HardHat, ShieldCheck, ArrowLeft, Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";

const MASTER_TG_URL = "https://master-tg.ai.studio/";
const LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_tirera-apps/artifacts/k26wdj9v_6AA30CA0-666C-40FF-9A0E-C79071141ABA.png";

const FEATURES = [
  { icon: ListChecks, title: "QCM interactifs", desc: "Entraînez-vous avec des questions à choix multiples et suivez votre progression." },
  { icon: ScanLine, title: "Scanner IA", desc: "Analysez et reconnaissez des documents et éléments grâce à l'intelligence artificielle." },
  { icon: Layers, title: "Flashcards", desc: "Révisez efficacement avec des cartes mémoire intelligentes." },
  { icon: Ruler, title: "Plans 2D", desc: "Consultez et travaillez sur des plans techniques en deux dimensions." },
  { icon: Box, title: "Visualisation 3D", desc: "Explorez vos projets et modèles en trois dimensions." },
  { icon: HardHat, title: "Modules BTP", desc: "Des outils métiers dédiés à la construction et au bâtiment." },
  { icon: ShieldCheck, title: "Administration", desc: "Gérez contenus, utilisateurs et paramètres depuis un espace dédié." },
];

const OpenButton = ({ testid, label = "Ouvrir Master TG" }) => (
  <a
    href={MASTER_TG_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="group inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-base font-bold text-navy shadow-[0_10px_40px_-10px_rgba(200,164,77,0.6)] transition-all hover:bg-gold-400 hover:-translate-y-0.5"
  >
    {label}
    <ExternalLink size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

export default function MasterTG() {
  return (
    <main data-testid="master-tg-page" className="bg-navy">
      <title>Master TG | TIRERA GROUP</title>
      <meta name="description" content="Master TG — la plateforme intelligente de TIRERA GROUP : QCM, scanner IA, flashcards, plans 2D, visualisation 3D et modules BTP." />
      <meta property="og:title" content="Master TG | TIRERA GROUP" />
      <meta property="og:image" content={LOGO} />

      {/* Hero */}
      <section className="relative overflow-hidden pt-28 pb-16 sm:pt-36 sm:pb-24">
        <div className="pointer-events-none absolute left-1/2 top-10 h-96 w-96 -translate-x-1/2 rounded-full bg-gold/20 blur-[120px]" />
        <div className="relative mx-auto max-w-5xl px-5 text-center sm:px-8">
          <Link to="/" data-testid="mastertg-back" className="mb-8 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white/80 backdrop-blur hover:bg-white/20">
            <ArrowLeft size={16} /> Retour à l'accueil
          </Link>

          <motion.img
            src={LOGO}
            alt="Master TG"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto mb-8 h-40 w-40 rounded-3xl object-cover shadow-2xl ring-1 ring-gold/30 sm:h-48 sm:w-48"
          />

          <motion.span
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-sm font-medium text-gold-400"
          >
            <Sparkles size={15} /> Plateforme intelligente TIRERA GROUP
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
            className="mt-5 font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl"
          >
            Master TG
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.26 }}
            className="mx-auto mt-4 max-w-2xl text-base text-white/70 sm:text-lg"
          >
            Une application complète : QCM, scanner IA, flashcards, plans 2D, visualisation 3D et modules BTP — réunis dans un seul outil.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.34 }}
            className="mt-9 flex flex-col items-center gap-3"
          >
            <OpenButton testid="mastertg-open-hero" />
            <span className="text-xs text-white/40">L'application s'ouvre dans un nouvel onglet — master-tg.ai.studio</span>
          </motion.div>
        </div>
      </section>

      {/* Fonctionnalités */}
      <section className="border-t border-white/10 bg-white/[0.02] py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <h2 className="text-center font-display text-2xl font-bold text-white sm:text-3xl">Tout ce que fait Master TG</h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-white/50">Toutes les fonctionnalités de l'application sont conservées à l'identique.</p>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  data-testid={`mastertg-feature-${i}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-gold/30 hover:bg-white/[0.05]"
                >
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-gold/15 text-gold-400">
                    <Icon size={22} />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-white">{f.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/60">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-14 flex flex-col items-center gap-3 text-center">
            <OpenButton testid="mastertg-open-bottom" label="Lancer l'application Master TG" />
            <span className="text-xs text-white/40">Accès complet à toutes les fonctionnalités</span>
          </div>
        </div>
      </section>
    </main>
  );
}
